import moment from 'moment';
import styled from 'styled-components';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Button, Fab, Tooltip, Typography } from '@mui/material';

//icons
import PrintIcon from '@mui/icons-material/Print';
import ExcelIcon from '@mui/icons-material/TableChart';
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-icon.svg';

//constants
import { NEXT_WEEK, PREV_WEEK, FACILITIES_VIEW_TYPE } from '../../constants';
import { MOMENT_DATE_FORMAT } from '../../constants/date_format';
import { colors } from '../../theme';

//comps
import GenerateDOM from './generateDOM';
import { generateConflictsLayout, generateFirstRow, generateTimeSlots, generateTimetableLayout, getColumnsObject } from './functions/layout';

//services
import { connect } from 'react-redux';
import { useAppSelector } from '../../hooks/hooks';

const ResponsiveGridLayout = WidthProvider(Responsive);

const Base = (props) => {
    const {
        config,
        timetables,
        conflicts,
        getInstructorTimetable,
        getSetTimetable,
        getRoomTimetable,
        handleWeekChange,
        week,
        timetableType,
        handleExcelExport,
        printSSRSReport,
        title,
        startDate,
        endDate,
        termSelected,
        weekTitle,
        timetableID,
    } = props;

    const appState = useAppSelector((state) => state.appState);

    const { viewType } = appState;

    //Monday should be Day 1 of the week
    moment.updateLocale('en', { week: { dow: 1 } });

    const termStartDate = moment(termSelected ? termSelected.startDate : startDate, MOMENT_DATE_FORMAT);
    const termStartWeek = termStartDate.week();

    const termEndDate = moment(termSelected ? termSelected.endDate : endDate, MOMENT_DATE_FORMAT);
    const termEndWeek = termEndDate.diff(termStartDate, 'week') + termStartWeek;
    const calendarWeek = termStartWeek + week;

    //clone the object as it should not be modified
    const year = moment(termStartDate).add(week, 'weeks').year();

    // All date operations will be relative to {year-01-20}, this way current date does not creep into the calculation at all (has been problematic)
    const dateRange = `${moment({
        year: year,
        month: 1,
        day: 20,
    })
        .day('Monday')
        .week(calendarWeek)
        .format('MMM DD')} - ${moment({
        year: year,
        month: 1,
        day: 20,
    })
        .day('Friday')
        .week(calendarWeek)
        .format('MMM DD')}`;

    const defaultLayout = [];
    const xAxisArr = generateFirstRow(defaultLayout, appState.viewType);
    const yAxisArr = generateTimeSlots(defaultLayout, config);
    let rglLayout = generateTimetableLayout(timetables, defaultLayout.length, xAxisArr, yAxisArr);

    //Add default layout
    rglLayout = defaultLayout.concat(rglLayout);

    //Add conflict layout if there are conflicts
    if (conflicts.length > 0) {
        rglLayout = generateConflictsLayout(rglLayout, yAxisArr.length, conflicts, xAxisArr, appState.viewType);
    }

    const layouts = { lg: rglLayout, sm: rglLayout };

    const cols = getColumnsObject(appState.viewType);

    return (
        <div>
            <Header>
                <TimetableTitle children={!title ? '' : title} />
                <Headline children={`${weekTitle} ${handleWeekChange ? '| ' + dateRange : ''}`} />

                {viewType !== FACILITIES_VIEW_TYPE && (
                    <PrintButton onClick={async () => await handleExcelExport()}>
                        <ExcelIcon />
                        Excel
                    </PrintButton>
                )}

                {printSSRSReport && viewType !== FACILITIES_VIEW_TYPE && (
                    <PrintButton onClick={async () => await printSSRSReport()}>
                        <PrintIcon />
                        Print
                    </PrintButton>
                )}

                {handleWeekChange && (
                    <>
                        {
                            <Tooltip title="Prev Week">
                                <StyledFab size="small" disabled={week === 0} onClick={async () => await handleWeekChange(PREV_WEEK)}>
                                    <ArrowIcon style={{ transform: 'rotate(90deg)' }} />
                                </StyledFab>
                            </Tooltip>
                        }

                        {
                            <Tooltip title="Next Week">
                                <StyledFab size="small" disabled={calendarWeek >= termEndWeek} onClick={async () => await handleWeekChange(NEXT_WEEK)}>
                                    <ArrowIcon style={{ transform: 'rotate(-90deg)' }} />
                                </StyledFab>
                            </Tooltip>
                        }
                    </>
                )}
            </Header>

            <Grid>
                <DefaultSpots
                    autoSize={true}
                    isDraggable={false}
                    isResizable={false}
                    preventCollision={true}
                    compactType={null}
                    className="layout"
                    layouts={layouts}
                    rowHeight={35}
                    cols={cols}
                    margin={[0, 0]}
                >
                    {GenerateDOM({
                        layouts,
                        getInstructorTimetable,
                        getSetTimetable,
                        getRoomTimetable,
                        handleWeekChange,
                        week,
                        timetableType,
                    })}
                </DefaultSpots>
            </Grid>
        </div>
    );
};

const Header = styled.div`
    display: flex;
    align-items: center;
    padding: 5px;
`;

const PrintButton = styled(Button)`
    && {
        color: ${colors.grey.medium};
        svg {
            margin-right: 4px;
        }
        &:hover {
            color: ${colors.blue.main};
        }
    }
`;

const StyledFab = styled(Fab)`
    && {
        width: 30px;
        height: 30px;
        min-height: auto;
        background: transparent;
        box-shadow: none;
        z-index: 1;
        &:hover {
            background-color: ${colors.blue.light};
        }
    }
`;

const TimetableTitle = styled(Typography)`
    && {
        padding: 3px 8px;
        background-color: ${colors.blue.light};
        border-radius: 6px;
        font-weight: 700;
        font-size: 18px;
        color: ${colors.blue.main};
        text-align: center;
        margin-right: 8px;
    }
`;

const Headline = styled(Typography)`
    && {
        font-size: 16px;
        font-weight: 400;
        flex: 1;
        color: ${colors.grey.dark};
    }
`;

const Grid = styled.div`
    position: relative;
    overflow: hidden;
    background: #fff;
    margin-bottom: 20px;

    .react-grid-layout {
        overflow: hidden;
    }

    .react-grid-item {
        overflow: visible;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: none;
    }
`;

const DefaultSpots = styled(ResponsiveGridLayout)`
    && {
        position: relative;
        z-index: 1;
    }
`;

const mapStateToProps = function (state) {
    return {
        schoolID: state.user.schoolId,
        termSchoolID: state.appState.term?.termSchoolId,
        termCampusId: state.appState.term?.termCampusId,
        termCode: state.appState.term?.termCode,
        startDate: state.appState.term?.startDate,
        endDate: state.appState.term?.endDate,
        accountID: state.user.id,
        firstName: state.user.firstName,
        lastName: state.user.lastName,
        accountType: state.user.accountType,
    };
};

export default connect(mapStateToProps)(Base);
